import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Plus, Edit, Trash2, X, Image } from 'lucide-react';

export default function AdminPrestations() {
  const { getAuthHeaders } = useAuth();
  const [prestations, setPrestations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [editingPrestation, setEditingPrestation] = useState(null);
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [categoriesImages, setCategoriesImages] = useState({});
  const [uploadingCat, setUploadingCat] = useState(null);
  const [formData, setFormData] = useState({
    nom: '', categorie: '', duree_minutes: 60, prix_euros: 0, description: ''
  });

  useEffect(() => { fetchPrestations(); fetchCategories(); fetchCategoriesImages(); }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/categories`);
      setCategories(Array.isArray(response.data.categories) ? response.data.categories : []);
    } catch (error) { console.error('Erreur:', error); }
  };

  const fetchCategoriesImages = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories/images`);
      const data = await res.json();
      setCategoriesImages(data || {});
    } catch (e) {}
  };

  const handleUploadCategoryImage = async (cat, file) => {
    setUploadingCat(cat);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const uploadRes = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload`, fd, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
      const newImages = { ...categoriesImages, [cat]: uploadRes.data.url };
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/categories/images`, newImages, { headers: getAuthHeaders() });
      setCategoriesImages(newImages);
    } catch (e) { alert("Erreur upload"); }
    setUploadingCat(null);
  };

  const handleRemoveCategoryImage = async (cat) => {
    const newImages = { ...categoriesImages };
    delete newImages[cat];
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/categories/images`, newImages, { headers: getAuthHeaders() });
      setCategoriesImages(newImages);
    } catch (e) { alert("Erreur suppression"); }
  };

  const fetchPrestations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/prestations`);
      setPrestations(response.data);
    } catch (error) { console.error('Erreur:', error); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPrestation) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/prestations/${editingPrestation.id}`, formData, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/prestations`, formData, { headers: getAuthHeaders() });
      }
      fetchPrestations(); closeModal();
    } catch (error) { console.error('Erreur:', error); alert("Erreur lors de l'enregistrement"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette prestation ?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/prestations/${id}`, { headers: getAuthHeaders() });
      fetchPrestations();
    } catch (error) { console.error('Erreur:', error); }
  };

  const openModal = (prestation = null) => {
    if (prestation) {
      setEditingPrestation(prestation);
      setFormData({ nom: prestation.nom, categorie: prestation.categorie, duree_minutes: prestation.duree_minutes, prix_euros: prestation.prix_euros, description: prestation.description });
    } else {
      setEditingPrestation(null);
      setFormData({ nom: '', categorie: categories.length > 0 ? categories[0] : '', duree_minutes: 60, prix_euros: 0, description: '' });
    }
    setShowModal(true);
  };

  const handleAddCategory = () => {
    const trimmed = newCategory.trim();
    if (trimmed && !categories.includes(trimmed)) {
      setCategories(prev => [...prev, trimmed]);
      setFormData(prev => ({ ...prev, categorie: trimmed }));
    }
    setNewCategory(''); setShowCategoryModal(false);
  };

  const closeModal = () => { setShowModal(false); setEditingPrestation(null); };

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="flex flex-wrap justify-between items-start gap-3 mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-serif">Gestion des Prestations</h1>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setShowImagesModal(true)}
              className="flex items-center space-x-1 border border-border text-foreground px-3 py-2 rounded-sm hover:bg-secondary text-sm">
              <Image size={16} /><span>Images</span>
            </button>
            <button onClick={() => setShowCategoryModal(true)} data-testid="create-category-btn"
              className="flex items-center space-x-1 border border-accent text-accent px-3 py-2 rounded-sm hover:bg-accent/10 text-sm">
              <Plus size={16} /><span>Catégorie</span>
            </button>
            <button onClick={() => openModal()} data-testid="create-prestation-btn"
              className="flex items-center space-x-1 bg-accent text-accent-foreground px-3 py-2 rounded-sm hover:opacity-90 text-sm">
              <Plus size={16} /><span>Prestation</span>
            </button>
          </div>
        </div>

        {/* Desktop : tableau */}
        <div className="hidden md:block bg-white border border-border rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-secondary">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium">Nom</th>
                <th className="px-6 py-3 text-left text-sm font-medium">Catégorie</th>
                <th className="px-6 py-3 text-left text-sm font-medium">Durée</th>
                <th className="px-6 py-3 text-left text-sm font-medium">Prix</th>
                <th className="px-6 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {prestations.map((prestation) => (
                <tr key={prestation.id}>
                  <td className="px-6 py-4 text-sm">{prestation.nom}</td>
                  <td className="px-6 py-4">
                    <span className="inline-block px-2 py-1 bg-accent/10 text-accent text-xs rounded">{prestation.categorie}</span>
                  </td>
                  <td className="px-6 py-4 text-sm">{prestation.duree_minutes} min</td>
                  <td className="px-6 py-4 text-sm font-medium">{prestation.prix_euros}€</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => openModal(prestation)} className="text-accent hover:text-accent/80 mr-3"><Edit size={18} /></button>
                    <button onClick={() => handleDelete(prestation.id)} className="text-red-600 hover:text-red-800"><Trash2 size={18} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile : cartes */}
        <div className="md:hidden space-y-3">
          {prestations.map((prestation) => (
            <div key={prestation.id} className="bg-white border border-border rounded-sm p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1 pr-3">
                  <p className="font-medium text-sm">{prestation.nom}</p>
                  <span className="inline-block px-2 py-0.5 bg-accent/10 text-accent text-xs rounded mt-1">{prestation.categorie}</span>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <button onClick={() => openModal(prestation)} className="p-2 text-accent hover:bg-accent/10 rounded-sm"><Edit size={18} /></button>
                  <button onClick={() => handleDelete(prestation.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-sm"><Trash2 size={18} /></button>
                </div>
              </div>
              <div className="flex items-center space-x-4 text-xs text-muted-foreground mt-2">
                <span>⏱ {prestation.duree_minutes} min</span>
                <span className="font-medium text-accent text-sm">{prestation.prix_euros}€</span>
              </div>
              {prestation.description && (
                <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{prestation.description}</p>
              )}
            </div>
          ))}
          {prestations.length === 0 && (
            <div className="bg-white border border-border rounded-sm p-8 text-center text-muted-foreground text-sm">
              Aucune prestation. Créez-en une !
            </div>
          )}
        </div>


        {/* Modal Images Catégories */}
        {showImagesModal && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
            <div className="bg-white rounded-t-lg sm:rounded-sm w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-border px-4 sm:px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl sm:text-2xl font-serif">Images des catégories</h2>
                <button onClick={() => setShowImagesModal(false)} className="p-1"><X size={24} /></button>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                {categories.map((cat) => (
                  <div key={cat} className="flex items-center gap-4 border border-border rounded-sm p-3">
                    <div className="w-16 h-16 flex-shrink-0 rounded-sm overflow-hidden bg-accent/10 flex items-center justify-center">
                      {categoriesImages[cat] ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${categoriesImages[cat]}`} alt={cat} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xs text-muted-foreground text-center px-1">Couleur par défaut</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm mb-2">{cat}</p>
                      <div className="flex gap-2">
                        <label className="cursor-pointer px-3 py-1.5 bg-accent text-accent-foreground text-xs rounded-sm hover:opacity-90">
                          {uploadingCat === cat ? 'Upload...' : 'Changer'}
                          <input type="file" accept="image/*" className="hidden"
                            onChange={(e) => e.target.files[0] && handleUploadCategoryImage(cat, e.target.files[0])} />
                        </label>
                        {categoriesImages[cat] && (
                          <button onClick={() => handleRemoveCategoryImage(cat)}
                            className="px-3 py-1.5 border border-red-300 text-red-600 text-xs rounded-sm hover:bg-red-50">
                            Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Modal Prestation */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
            <div className="bg-white rounded-t-lg sm:rounded-sm w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-border px-4 sm:px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl sm:text-2xl font-serif">{editingPrestation ? 'Modifier' : 'Nouvelle'} Prestation</h2>
                <button onClick={closeModal} className="p-1"><X size={24} /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nom</label>
                  <input type="text" value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} required
                    className="w-full px-4 py-2 border border-input rounded-sm focus:outline-none focus:ring-2 focus:ring-ring text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Catégorie</label>
                  <select value={formData.categorie} onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                    className="w-full px-4 py-2 border border-input rounded-sm focus:outline-none focus:ring-2 focus:ring-ring text-sm">
                    {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-2">Durée (min)</label>
                    <input type="number" value={formData.duree_minutes} onChange={(e) => setFormData({ ...formData, duree_minutes: parseInt(e.target.value) })} required
                      className="w-full px-4 py-2 border border-input rounded-sm focus:outline-none focus:ring-2 focus:ring-ring text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Prix (€)</label>
                    <input type="number" step="0.01" value={formData.prix_euros} onChange={(e) => setFormData({ ...formData, prix_euros: parseFloat(e.target.value) })} required
                      className="w-full px-4 py-2 border border-input rounded-sm focus:outline-none focus:ring-2 focus:ring-ring text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3"
                    className="w-full px-4 py-2 border border-input rounded-sm focus:outline-none focus:ring-2 focus:ring-ring text-sm" />
                </div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={closeModal} className="px-4 py-2 border border-border rounded-sm hover:bg-secondary text-sm">Annuler</button>
                  <button type="submit" className="px-4 py-2 bg-accent text-accent-foreground rounded-sm hover:opacity-90 text-sm">Enregistrer</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Catégorie */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
            <div className="bg-white rounded-t-lg sm:rounded-sm w-full sm:max-w-md">
              <div className="px-4 sm:px-6 py-4 border-b border-border flex justify-between items-center">
                <h2 className="text-xl font-serif">Nouvelle Catégorie</h2>
                <button onClick={() => setShowCategoryModal(false)}><X size={24} /></button>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nom de la catégorie</label>
                  <input type="text" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}
                    data-testid="new-category-input" placeholder="Ex: Maquillage, Soins du corps..."
                    className="w-full px-4 py-2 border border-input rounded-sm focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()} autoFocus />
                </div>
                {categories.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Catégories existantes :</p>
                    <div className="flex flex-wrap gap-2">
                      {categories.map((cat) => (
                        <span key={cat} className="inline-block px-3 py-1 bg-accent/10 text-accent text-xs rounded-full">{cat}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowCategoryModal(false)} className="px-4 py-2 border border-border rounded-sm hover:bg-secondary text-sm">Annuler</button>
                  <button type="button" onClick={handleAddCategory} data-testid="save-category-btn" disabled={!newCategory.trim()}
                    className="px-4 py-2 bg-accent text-accent-foreground rounded-sm hover:opacity-90 disabled:opacity-50 text-sm">Ajouter</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
